import { AssessmentResponse } from '@/types/assessment';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { isValidUUID } from '../validation/validationUtils';
import { saveResponsesToLocalStorage } from '../storage/localStorageService';

/**
 * Saves responses to Supabase database
 */
export const saveResponsesToDatabase = async (userId: string, responses: AssessmentResponse[]): Promise<{
  successCount: number;
  errorCount: number;
}> => {
  // Validate that user ID is a valid UUID for Supabase
  if (!isValidUUID(userId)) {
    console.warn('User ID is not a valid UUID. Skipping Supabase save.');
    return { successCount: 0, errorCount: responses.length };
  }

  // Use single inserts for better error handling
  let successCount = 0;
  let errorCount = 0;
  
  for (const response of responses) {
    try {
      // Create data object with the correct schema type
      const responseData = {
        user_id: userId,
        question_id: response.questionId,
        answer: response.answer,
        order_id: response.orderId || null,
      };
      
      // Cast as any to bypass TypeScript errors
      const { error } = await supabase
        .from('user_responses')
        .upsert(responseData as any, { onConflict: 'user_id,question_id' });
        
      if (error) {
        console.error('Error saving response for question', response.questionId, ':', error);
        errorCount++;
      } else {
        successCount++;
      }
    } catch (e) {
      console.error('Exception saving response for question', response.questionId, ':', e);
      errorCount++;
    }
  }

  return { successCount, errorCount };
};

/**
 * Comprehensive function to save responses with local and remote storage
 */
export const saveResponses = async (userId: string, responses: AssessmentResponse[]): Promise<void> => {
  try {
    if (!userId) {
      console.error('No user ID provided for saving responses');
      toast.warning('Unable to save responses to database - no user ID');
      return;
    }
    
    // Always save to localStorage first as a backup
    saveResponsesToLocalStorage(userId, responses);

    // Check if user is authenticated with Supabase
    const { data: { session }, error: authError } = await supabase.auth.getSession();
    if (authError) {
      console.error('Authentication error when saving responses:', authError);
      toast.warning('Authentication issue detected. Your responses are saved locally.');
      return;
    }

    if (!session) {
      console.warn('No authenticated session found. Using local storage only.');
      toast.warning('No authenticated session found. Your responses are saved locally.');
      return;
    }

    // Get current user from session
    const currentUser = session.user;
    if (!currentUser) {
      console.warn('No user found in session. Using local storage only.');
      toast.warning('No user found in session. Your responses are saved locally.');
      return;
    }

    // Verify user ID matches the authenticated user
    if (userId !== currentUser.id && isValidUUID(userId)) {
      console.warn('User ID mismatch. Using authenticated user ID instead.');
      userId = currentUser.id;
    }

    console.log('Authenticated user found, continuing with database save');

    // Save responses to Supabase
    console.log('Attempting to save responses to Supabase for user ID:', userId);
    
    const { successCount, errorCount } = await saveResponsesToDatabase(userId, responses);

    if (errorCount > 0) {
      console.warn(`Saved ${successCount} responses, but ${errorCount} failed to save.`);
      toast.warning(`Saved ${successCount} responses, but ${errorCount} failed to save. Your responses are also saved locally.`);
    } else if (successCount > 0) {
      console.log('All responses saved successfully to Supabase!');
      toast.success('All responses saved successfully!');
    } else {
      console.warn('No responses were saved to the database.');
      toast.warning('No responses were saved to the database. Your responses are saved locally.');
    }
  } catch (error) {
    console.error('Error in saveResponses function:', error);
    toast.error('An error occurred while saving responses, but they were saved locally');
    // We don't throw here because we've already saved to localStorage
  }
};
